import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Upload, Copy, Trash2, Check } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, apiError, BACKEND_URL } from "../../lib/api";

export const mediaSrc = (url) => (url && url.startsWith("/") ? `${BACKEND_URL}${url}` : url);

function useMedia() {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const load = () => api.get("/admin/media").then(({ data }) => setItems(data.items)).catch(() => {});
  useEffect(() => { load(); }, []);
  const upload = async (file) => {
    if (!file) return null;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
      return data.url;
    } catch (e) {
      toast.error(apiError(e));
      return null;
    } finally {
      setUploading(false);
    }
  };
  const remove = async (name) => {
    try { await api.delete(`/admin/media/${name}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  return { items, uploading, upload, remove, load };
}

function Grid({ items, onPick, onRemove }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(null);
  const copy = (url) => { navigator.clipboard.writeText(url); setCopied(url); setTimeout(() => setCopied(null), 1200); };
  if (items.length === 0) return <p className="text-sm text-muted-foreground py-8 text-center">{t("cms.no_media")}</p>;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
      {items.map((m) => (
        <div key={m.name} className="group relative rounded-lg border border-border overflow-hidden bg-secondary" data-testid="media-item">
          <button type="button" onClick={() => onPick?.(m.url)} className="block aspect-square w-full">
            <img src={mediaSrc(m.url)} alt="" className="h-full w-full object-cover" loading="lazy" />
          </button>
          <div className="absolute inset-x-0 bottom-0 flex justify-between gap-1 p-1 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
            <button type="button" onClick={() => copy(m.url)} className="text-white p-1" title="Copy URL">
              {copied === m.url ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
            {onRemove && (
              <button type="button" onClick={() => onRemove(m.name)} className="text-white p-1" title="Delete" data-testid="media-delete">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function UploadButton({ onFile, uploading }) {
  const { t } = useTranslation();
  const ref = useRef();
  return (
    <>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onFile(f); }} data-testid="media-upload-input" />
      <Button type="button" variant="outline" disabled={uploading} onClick={() => ref.current?.click()} data-testid="media-upload-button">
        <Upload className="h-4 w-4 mr-2" /> {uploading ? "…" : t("cms.upload")}
      </Button>
    </>
  );
}

export default function MediaLibrary() {
  const { t } = useTranslation();
  const { items, uploading, upload, remove } = useMedia();
  const [q, setQ] = useState("");
  const filtered = q ? items.filter((m) => m.name.toLowerCase().includes(q.toLowerCase())) : items;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <UploadButton onFile={upload} uploading={uploading} />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("cms.search_media")} className="max-w-xs" data-testid="media-search" />
      </div>
      <Grid items={filtered} onRemove={remove} />
    </div>
  );
}

export function MediaPicker({ open, onClose, onSelect }) {
  const { t } = useTranslation();
  const { items, uploading, upload } = useMedia();
  const pick = (url) => { onSelect(url); onClose(); };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{t("cms.choose_image")}</DialogTitle></DialogHeader>
        <UploadButton onFile={async (f) => { const u = await upload(f); if (u) pick(u); }} uploading={uploading} />
        <div className="max-h-[50vh] overflow-y-auto mt-2">
          <Grid items={items} onPick={pick} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
